import type {
  CrossWorkspaceDuplicateMatch,
  CrossWorkspaceDuplicateResponse,
} from "./api";

const DISTRIBUTION_LABEL: Record<
  CrossWorkspaceDuplicateMatch["distributionStatus"],
  string
> = {
  DISTRIBUTED: "已分发",
  RECEIVED: "已收到，未分发",
  CLOSED: "已关闭",
  QUARANTINED: "已隔离",
};

export function CrossWorkspaceDuplicateNotice({
  result,
  loading,
  error,
  acknowledged,
  onAcknowledgedChange,
}: {
  result: CrossWorkspaceDuplicateResponse | null;
  loading: boolean;
  error: string;
  acknowledged: boolean;
  onAcknowledgedChange: (value: boolean) => void;
}) {
  if (loading) return null;
  if (error) {
    return (
      <div className="cross-workspace-check is-warning" role="status">
        {error}
      </div>
    );
  }
  if (!result) return null;
  if (!result.coverage.customerEmail || !result.coverage.productIdentity)
    return null;
  if (!result.matches.length) return null;

  const requiresAcknowledgement = result.matches.some(
    ({ distributionStatus }) => distributionStatus === "DISTRIBUTED",
  );
  const grouped = new Map<string, CrossWorkspaceDuplicateMatch[]>();
  for (const match of result.matches) {
    const rows = grouped.get(match.workspaceId) ?? [];
    rows.push(match);
    grouped.set(match.workspaceId, rows);
  }
  return (
    <section
      className={
        "cross-workspace-duplicates " +
        (requiresAcknowledgement ? "is-danger" : "is-warning")
      }
      role="alert"
      aria-label="跨空间重复提醒"
    >
      <header>
        <strong>跨空间重复提醒</strong>
        <span>
          {result.matches.length} 条 · 同一客户邮箱 · 产品 CAS/名称命中 ·{" "}
          {result.windowDays} 天内
        </span>
      </header>
      <p>
        其他工作空间已经收到同一客户、同一产品的询单。请核对后再决定是否继续分发；
        系统不会自动合并或关闭当前询单。
      </p>
      <div className="cross-workspace-duplicate-groups">
        {[...grouped.entries()].map(([workspaceId, rows]) => (
          <div className="cross-workspace-duplicate-group" key={workspaceId}>
            <div className="cross-workspace-duplicate-group-title">
              <strong>{rows[0]!.workspaceName}</strong>
              <span>{rows.length} 条</span>
            </div>
            {rows.slice(0, 3).map((match) => (
              <div
                className="cross-workspace-duplicate-row"
                key={match.inquiryId}
              >
                <div>
                  <strong>
                    {match.productName ??
                      (match.cas ? `CAS ${match.cas}` : "产品待确认")}
                  </strong>
                  <small>
                    {match.cas ? `CAS ${match.cas} · ` : ""}
                    {match.matchBasis === "EXACT_CAS"
                      ? "CAS 相同，确定重复"
                      : "产品名称匹配，疑似重复"}
                  </small>
                </div>
                <div>
                  <span>{formatDate(match.submittedAt)}</span>
                  <small>
                    {match.siteName ?? match.workspaceCode} ·{" "}
                    {match.customerEmailMasked}
                  </small>
                </div>
                <div className="cross-workspace-duplicate-status">
                  <strong>
                    {DISTRIBUTION_LABEL[match.distributionStatus]}
                  </strong>
                  <small>
                    {match.caseNumber
                      ? `案件 ${match.caseNumber}`
                      : match.quantity
                        ? `数量 ${match.quantity}`
                        : "尚未建立案件"}
                  </small>
                </div>
              </div>
            ))}
            {rows.length > 3 ? (
              <small className="cross-workspace-duplicate-more">
                另有 {rows.length - 3} 条同类询单
              </small>
            ) : null}
          </div>
        ))}
      </div>
      {requiresAcknowledgement ? (
        <label className="cross-workspace-duplicate-ack">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => onAcknowledgedChange(event.target.checked)}
          />
          <span>我已知晓其他空间已分发，仍要在当前空间分发</span>
        </label>
      ) : null}
    </section>
  );
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
